import type { ReactNode } from 'react';
import { paraMetni, sayiMetni, tarihMetni, yuzdeMetni } from './bicim';

/**
 * S334: rapor tablosunun kolon sözleşmesi ve SAF hesapları (sıralama, toplam, gruplama, etiket).
 * Bileşenden ayrı durur: tablo ve kart kipi aynı hesabı kullanır, testler tablo çizmeden sınar.
 */
export type KolonTuru = 'metin' | 'kod' | 'para' | 'sayi' | 'yuzde' | 'tarih';

/** Hücrenin ham değeri. Tarih 'YYYY-MM-DD...' metnidir (Date DEĞİL, bkz. `tarihMetni`). */
export type HucreDegeri = string | number | null | undefined;

export interface RaporKolonu<S> {
  id: string;
  baslik: string;
  tur: KolonTuru;
  deger: (satir: S) => HucreDegeri;
  /**
   * `true`: süzülmüş TÜM satırların `deger` toplamı. Fonksiyon: toplamı kendisi hesaplar
   * (oranlar satır oranlarının toplamı değil, pay ve paydanın toplamından yeniden hesaplanır).
   * Aynı kural grup ara toplamında grubun satırlarıyla çalışır.
   */
  toplam?: true | ((satirlar: readonly S[]) => HucreDegeri);
  /** Değeri metin yerine düğümle çizer (B/A işareti gibi). Toplam ve ara toplam hücresinde de çağrılır. */
  hucre?: (deger: HucreDegeri) => ReactNode;
  /** Varsayılan: sayısal türler sağa, ötekiler sola. */
  hizala?: 'sol' | 'sag' | 'orta';
  /** Sabit genişlik (px). Verilmezse veriden ölçülür. */
  genislik?: number;
  /** Artan yatay yeri bu kolon alır. Hiçbir kolonda yoksa ilk serbest metin kolonu alır. */
  esnek?: boolean;
  /** Telefondaki kartta bu kolonun yeri (bkz. `KartRolu`). Verilmezse `kartRolu` varsayılanı. */
  kart?: KartRolu;
  /** Karttaki küçük etiket. `ikincil` hep etiketlidir (varsayılan `baslik`); ötekiler yalnız verilirse. */
  kartEtiketi?: string;
}

/**
 * Kolonun telefon kartındaki yeri. Kart en çok üç satırdır (Yerlice `BelgeSatirKartlari` kalıbı):
 *   1. satır: `baslik` (solda, kırpılabilen TEK alan) + `olcu` ve `ek` (sağda, kırpılmaz)
 *   2. satır: `alt` (solda küçük, " · " ile birleşik) + `olcuAlt` (sağda küçük)
 *   3. satır: `ikincil` (etiketli küçük ölçüler, sığmazsa alt satıra sarar)
 * `ek`: ölçünün yanındaki işaret (B/A). `gizli`: kartta yok (tabloda var).
 */
export type KartRolu = 'baslik' | 'olcu' | 'olcuAlt' | 'ek' | 'alt' | 'ikincil' | 'gizli';

/** Rol verilmemiş kolonun karttaki yeri: ilk kolon başlık, toplanan sayılar ikincil, gerisi alt. */
export function kartRolu<S>(kolon: RaporKolonu<S>, sira: number): KartRolu {
  if (kolon.kart) return kolon.kart;
  if (sira === 0) return 'baslik';
  return sayisalMi(kolon.tur) && kolon.toplam ? 'ikincil' : 'alt';
}

export interface Siralama {
  id: string;
  /** 1 artan, -1 azalan. */
  yon: 1 | -1;
}

export interface GrupAyari<S> {
  /** Gruplanan alanın kimliği. Aynı `id`li kolon tabloda GÖSTERİLMEZ (grup satırında zaten yazıyor). */
  id: string;
  deger: (satir: S) => string | null | undefined;
  /** Grup satırındaki sayının birimi: "12 ürün". */
  birim?: string;
}

export interface Grup<S> {
  ad: string;
  satirlar: S[];
}

export const TANIMSIZ = '(Tanımsız)';

export const sayisalMi = (tur: KolonTuru) => tur === 'para' || tur === 'sayi' || tur === 'yuzde';

/** Boş değer boş hücredir (0 yazılır, null yazılmaz). */
export function hucreMetni(tur: KolonTuru, v: HucreDegeri): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'number') {
    if (tur === 'para') return paraMetni(v);
    if (tur === 'yuzde') return yuzdeMetni(v);
    return sayiMetni(v);
  }
  return tur === 'tarih' ? tarihMetni(v) : v;
}

const sayiDegeri = (v: HucreDegeri) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Kolonun `satirlar` üzerindeki toplamı; kolon toplanmıyorsa `undefined`. */
export function kolonToplami<S>(kolon: RaporKolonu<S>, satirlar: readonly S[]): HucreDegeri {
  if (!kolon.toplam) return undefined;
  if (typeof kolon.toplam === 'function') return kolon.toplam(satirlar);
  let t = 0;
  for (const s of satirlar) t += sayiDegeri(kolon.deger(s));
  return t;
}

const TR = new Intl.Collator('tr');

/** Boş değer yönden bağımsız EN SONDA; metin Türkçe sıralanır. Kaynak dizi değişmez. */
export function siralaSatirlar<S>(
  satirlar: readonly S[],
  kolonlar: readonly RaporKolonu<S>[],
  siralama: Siralama | null,
): S[] {
  const kolon = siralama ? kolonlar.find((k) => k.id === siralama.id) : undefined;
  if (!siralama || !kolon) return satirlar.slice();
  const { yon } = siralama;
  // Değer bir kez okunur: 192 bin satırda karşılaştırma başına `deger()` çağırmak sıralamayı ikiye katlar.
  const cift = satirlar.map((s) => ({ s, v: kolon.deger(s) }));
  cift.sort((a, b) => {
    const x = a.v, y = b.v;
    const xBos = x === null || x === undefined || x === '';
    const yBos = y === null || y === undefined || y === '';
    if (xBos || yBos) return xBos === yBos ? 0 : xBos ? 1 : -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * yon;
    return TR.compare(String(x), String(y)) * yon;
  });
  return cift.map((c) => c.s);
}

/**
 * Satırları gruplar. Grup sırası: sıralanan kolon TOPLANIYORSA ara toplama göre (yön dahil), değilse
 * ada göre ve boş değerlerin grubu "(Tanımsız)" en sonda. Grup içi sıra `satirlar`ın sırasıdır.
 */
export function gruplaSatirlar<S>(
  satirlar: readonly S[],
  grup: GrupAyari<S>,
  kolonlar: readonly RaporKolonu<S>[],
  siralama: Siralama | null,
): Grup<S>[] {
  const harita = new Map<string, S[]>();
  for (const s of satirlar) {
    const ad = grup.deger(s) || TANIMSIZ;
    const liste = harita.get(ad);
    if (liste) liste.push(s);
    else harita.set(ad, [s]);
  }
  const gruplar = Array.from(harita, ([ad, liste]) => ({ ad, satirlar: liste }));
  const kolon = siralama ? kolonlar.find((k) => k.id === siralama.id) : undefined;
  if (siralama && kolon?.toplam) {
    const alt = new Map(gruplar.map((g) => [g.ad, sayiDegeri(kolonToplami(kolon, g.satirlar))]));
    gruplar.sort((a, b) => (alt.get(a.ad)! - alt.get(b.ad)!) * siralama.yon);
  } else {
    gruplar.sort(
      (a, b) => Number(a.ad === TANIMSIZ) - Number(b.ad === TANIMSIZ) || TR.compare(a.ad, b.ad),
    );
  }
  return gruplar;
}

const SAYI = new Intl.NumberFormat('tr-TR');

/**
 * Toplam satırının ETİKETİ: "bu toplam neyi kapsıyor?" (Yerlice `lib/footerEtiket.ts` kalıbı).
 * Toplam süzüleni toplar; etiket "Genel Toplam" kalsaydı ekran genel toplam DEYİP süzülmüş sayı
 * yazardı. `zorla`: satır sayısı azalmadan da daralan süzgeçler (lokasyon gibi sunucu süzgeci) için.
 */
export function toplamEtiketi(taban: string, suzulen: number, ham: number, zorla = false): string {
  return zorla || suzulen < ham ? `Süzülen toplam (${SAYI.format(suzulen)} satır)` : taban;
}

/**
 * Grupların açık / kapalı durumu. "Tümünü aç" grup adlarını bilmeden çalışsın diye küme değil
 * varsayılan + istisna tutulur (süzgeç değişip yeni grup doğunca o da varsayılana uyar).
 */
export interface GrupAcikligi {
  hepsiAcik: boolean;
  istisna: ReadonlySet<string>;
}

export const TUMU_KAPALI: GrupAcikligi = { hepsiAcik: false, istisna: new Set() };
export const TUMU_ACIK: GrupAcikligi = { hepsiAcik: true, istisna: new Set() };

export const grupAcikMi = (a: GrupAcikligi, ad: string) => a.hepsiAcik !== a.istisna.has(ad);

export function grupCevir(a: GrupAcikligi, ad: string): GrupAcikligi {
  const istisna = new Set(a.istisna);
  if (!istisna.delete(ad)) istisna.add(ad);
  return { hepsiAcik: a.hepsiAcik, istisna };
}

// ── kolon genişliği ─────────────────────────────────────────────────────────────
export const DOLGU = 22;
const MONO_PX = 7.9; // JetBrains Mono 13px: harf ilerlemesi 0,6 em
const HUCRE_YAZISI = '13px "Inter Tight", system-ui, sans-serif';
const BASLIK_YAZISI = '600 12px "Inter Tight", system-ui, sans-serif';
const bosMu = (v: HucreDegeri) => v === null || v === undefined || v === '';

let olcer: CanvasRenderingContext2D | null | undefined;

/**
 * Metnin çizim genişliği (px). Tarayıcıda canvas ile ÖLÇÜLÜR; canvas yoksa (jsdom) harf sınıfından
 * kestirilir (büyük harf ve rakam geniş, küçük harf dar).
 */
export function yaziGenisligi(metin: string, yazi: string): number {
  if (olcer === undefined) {
    // jsdom'da `getContext` "not implemented" hatası basar; sınıfın varlığına bakılır.
    olcer = typeof CanvasRenderingContext2D === 'undefined' ? null : document.createElement('canvas').getContext('2d');
  }
  if (olcer) {
    olcer.font = yazi;
    return olcer.measureText(metin).width;
  }
  let px = 0;
  for (const h of metin) px += h === ' ' || h === '.' || h === ',' ? 3.6 : h === h.toLocaleLowerCase('tr-TR') && !/\d/.test(h) ? 6.4 : 8.2;
  return px;
}

/**
 * Kolon genişlikleri (px), TÜM satırlardan. Her satır biçimlenip ölçülmez: sayısal kolonda yalnız uç
 * değerler (en büyük, en küçük, toplam), metin kolonunda yalnız EN UZUN metin ölçülür. 192 bin satırı
 * tek tek ölçmek yüzlerce ms tutardı. En uzun metin her zaman en geniş metin değildir; fark birkaç
 * pikseldir, taşan hücre üç noktayla kesilir ve tamamı `title`da durur.
 */
export function genislikler<S>(
  kolonlar: readonly RaporKolonu<S>[],
  satirlar: readonly S[],
  ustSatirlar: readonly S[],
  gruplu: boolean,
): number[] {
  return kolonlar.map((k, i) => {
    // Gruplu görünümde grup içi satırın ilk hücresi 16 px içeriden başlar (`.rt-ic`).
    const girinti = gruplu && i === 0 ? 16 : 0;
    return (k.genislik ?? olc(k)) + girinti;
  });

  function olc(k: RaporKolonu<S>): number {
    // +14: sıralama oku.
    const baslik = Math.ceil(yaziGenisligi(k.baslik, BASLIK_YAZISI) + DOLGU + 14);
    if (k.tur === 'tarih') return Math.max(baslik, 96);
    if (sayisalMi(k.tur) && !k.hucre) {
      let enk = 0, enb = 0, arti = 0, eksi = 0;
      const gor = (v: HucreDegeri) => {
        if (typeof v !== 'number') return;
        if (v < enk) enk = v;
        if (v > enb) enb = v;
        if (v > 0) arti += v; else eksi += v;
      };
      for (const s of satirlar) gor(k.deger(s));
      for (const s of ustSatirlar) gor(k.deger(s));
      const adaylar: HucreDegeri[] = [enk, enb, kolonToplami(k, satirlar)];
      // Ara toplam genel toplamdan GENİŞ olabilir (artı ve eksi gruplar birbirini götürür).
      if (gruplu && k.toplam === true) adaylar.push(arti, eksi);
      let harf = 0;
      for (const a of adaylar) harf = Math.max(harf, hucreMetni(k.tur, a).length);
      return Math.max(baslik, Math.ceil(harf * MONO_PX + DOLGU));
    }
    let enUzun = '', enUzunUst = '';
    for (const s of satirlar) {
      const v = k.deger(s);
      if (!bosMu(v) && String(v).length > enUzun.length) enUzun = String(v);
    }
    // Üst satırlar ("Devreden") italik basılır: ayrı ölçülür.
    for (const s of ustSatirlar) {
      const v = k.deger(s);
      if (!bosMu(v) && String(v).length > enUzunUst.length) enUzunUst = String(v);
    }
    if (k.tur === 'kod') {
      return Math.min(240, Math.max(baslik, Math.ceil(Math.max(enUzun.length, enUzunUst.length) * MONO_PX + DOLGU)));
    }
    const yazi = Math.max(yaziGenisligi(enUzun, HUCRE_YAZISI), yaziGenisligi(enUzunUst, `italic ${HUCRE_YAZISI}`));
    // +6: en uzun metin en geniş metin olmayabilir.
    return Math.min(340, Math.max(baslik, 72, Math.ceil(yazi + DOLGU + 6)));
  }
}
